import React, { useState, useEffect } from 'react';
import { Loader2, X, AlertCircle } from 'lucide-react';

interface EmmaTypingIndicatorProps {
  status: 'thinking' | 'searching' | 'processing';
  message?: string;
  sourcesChecked?: number;
  elapsedTime?: number;
  onCancel?: () => void;
}

/**
 * Transparent loading state for Emma's research process
 * Visual Design:
 * - Shows animated dots: "Emma is thinking..."
 * - Progress indicator: "Checking 12 sources..."
 * - Cancel button after 5 seconds
 * - Timeout warning after 15 seconds
 * EMMA-HARDEN-002: Cosellus green branding (#5CB800)
 */
export function EmmaTypingIndicator({ 
  status, 
  message,
  sourcesChecked = 0,
  elapsedTime = 0,
  onCancel
}: EmmaTypingIndicatorProps) {
  const [currentElapsed, setCurrentElapsed] = useState(elapsedTime);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentElapsed(prev => prev + 100);
    }, 100);

    return () => clearInterval(timer);
  }, []);

  const getMessage = () => {
    if (message) return message;
    switch (status) {
      case 'searching':
        return sourcesChecked > 0 
          ? `Checking ${sourcesChecked} sources...`
          : 'Searching for sources...';
      case 'processing':
        return 'Processing information...';
      default:
        return 'Emma is thinking...';
    }
  };

  const showCancelButton = currentElapsed > 5000;
  const showTimeoutWarning = currentElapsed > 15000;

  return (
    <div 
      className="flex items-start gap-3 p-4 bg-emma-50 rounded-lg border border-emma-200"
      role="status"
      aria-label="Emma is typing"
    >
      {/* Animated Spinner */}
      <Loader2 className="w-5 h-5 text-emma-600 animate-spin flex-shrink-0 mt-0.5" />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Status Message */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-sm text-gray-700 font-medium leading-relaxed">
            {message || getMessage()}
          </p>
          <span className="text-xs text-gray-500 font-mono flex-shrink-0">
            {(currentElapsed / 1000).toFixed(1)}s
          </span>
        </div>

        {/* Animated Dots */}
        <div className="flex items-center gap-1 mb-2">
          <div className="w-2 h-2 bg-emma-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-emma-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-emma-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>

        {/* Progress Bar (visual feedback) */}
        <div className="w-full h-1 bg-emma-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emma-600 rounded-full transition-all duration-300"
            style={{ 
              width: `${Math.min((currentElapsed / 20000) * 100, 90)}%`,
              transition: 'width 0.3s ease-out'
            }}
          />
        </div>

        {/* Cancel Button (after 5s) */}
        {showCancelButton && onCancel && (
          <button
            onClick={onCancel}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <X className="w-4 h-4" />
            Cancel request
          </button>
        )}

        {/* Timeout Warning (after 15s) */}
        {showTimeoutWarning && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  Taking longer than usual
                </p>
                <p className="text-xs text-amber-700">
                  Emma is working on a complex question. You can wait for the response or try asking something simpler.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Source Tracking (if in searching status) */}
        {status === 'searching' && sourcesChecked > 0 && (
          <div className="mt-2 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(sourcesChecked * 10, 100)}%` }}
                />
              </div>
              <span className="font-mono">{sourcesChecked} sources</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Simple dot animation for minimal loading states
 */
export function AnimatedDots() {
  return (
    <span className="inline-flex gap-1">
      <span className="animate-bounce inline-block">.</span>
      <span className="animate-bounce inline-block" style={{ animationDelay: '0.1s' }}>.</span>
      <span className="animate-bounce inline-block" style={{ animationDelay: '0.2s' }}>.</span>
    </span>
  );
}
